import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Q8Breakpoints } from 'core';
import { EMPTY, catchError, distinctUntilChanged, map, switchMap, timer } from 'rxjs';
import { NewCustomerCompany } from '../../../interfaces/new-customer.model';
import { NewCustomerService } from '../../../services/new-customer.service';
import { isValidTaxCode, isValidVat, searchTypeLabel, searchValue } from '../../../utils/new-customer.utils';
import { NewCustomerBanner } from '../new-customer-banner/new-customer-banner';
import { NewCustomerFilters, NewCustomerFiltersData, NewCustomerFiltersValue } from '../new-customer-filters/new-customer-filters';

// Aziende trovate: si sceglie col radio, «Vai al dettaglio» apre i dati completi
@Component({
  selector: 'ord-new-customer-results',
  imports: [
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    NewCustomerBanner,
    NewCustomerFilters,
    ReactiveFormsModule,
  ],
  templateUrl: './new-customer-results.html',
  styleUrl: './new-customer-results.scss',
})
export class NewCustomerResults implements OnInit {
  readonly flow = inject(NewCustomerService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly destroyRef = inject(DestroyRef);

  readonly textControl = new FormControl('', { nonNullable: true });

  // Su mobile i filtri si aprono in un dialog, da tablet in su in una scheda sopra i risultati
  readonly isMobile = signal(false);
  readonly filtersOpen = signal(false);
  private filtersDialogRef?: MatDialogRef<NewCustomerFilters, NewCustomerFiltersValue>;

  readonly typeLabel = computed(() => searchTypeLabel(this.flow.searchType()));
  readonly hasFilters = computed(() => !!this.flow.searchFilter().comune || !!this.flow.searchFilter().provincia);
  readonly countLabel = computed(() => {
    const count = this.flow.companies()?.items.length ?? 0;
    return count === 1 ? 'La ricerca ha prodotto 1 risultato' : `La ricerca ha prodotto ${count} risultati`;
  });

  // Comuni e province sono quelli delle aziende trovate
  readonly comuni = computed(() => this.distinct((this.flow.companies()?.items ?? []).map(company => company.comune)));
  readonly province = computed(() => this.distinct((this.flow.companies()?.items ?? []).map(company => company.provincia)));
  readonly filtersValue = computed<NewCustomerFiltersValue>(() => ({
    comune: this.flow.searchFilter().comune ?? '',
    provincia: this.flow.searchFilter().provincia ?? '',
  }));

  ngOnInit(): void {
    const type = this.flow.searchType();
    this.textControl.setValue(searchValue(this.flow.searchRequest()), { emitEvent: false });
    this.textControl.valueChanges
      .pipe(
        map(text => type === 'codiceFiscale' ? text.trim().toUpperCase() : text.trim()),
        distinctUntilChanged(),
        switchMap(text => {
          this.flow.company.set(null);
          if (!text || (type === 'partitaIva' && !isValidVat(text)) || (type === 'codiceFiscale' && !isValidTaxCode(text))) {
            return EMPTY;
          }

          return timer(400).pipe(
            switchMap(() => this.flow.search({ [type]: text })),
            catchError((error: unknown) => {
              const message = this.flow.errorMessage(error);
              if (message) {
                this.snackBar.open(message, 'Chiudi', { duration: 5000 });
              }
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.breakpointObserver.observe(`(max-width: ${Q8Breakpoints.tablet - 1}px)`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ matches }) => {
        this.isMobile.set(matches);
        if (matches) {
          this.filtersOpen.set(false);
        } else {
          this.filtersDialogRef?.close();
        }
      });
  }

  isSelected(company: NewCustomerCompany): boolean {
    return this.flow.company()?.companyId === company.companyId;
  }

  select(company: NewCustomerCompany): void {
    this.flow.selectCompany(company);
  }

  openDetail(event: Event, company: NewCustomerCompany): void {
    event.stopPropagation();
    this.flow.selectCompany(company);
    this.flow.go('detail');
  }

  // I filtri servono alla ricerca per ragione sociale, l'unica con più risultati
  openFilters(): void {
    if (!this.isMobile()) {
      this.filtersOpen.update(open => !open);
      return;
    }
    if (this.filtersDialogRef) {
      return;
    }

    this.filtersDialogRef = this.dialog.open<NewCustomerFilters, NewCustomerFiltersData, NewCustomerFiltersValue>(NewCustomerFilters, {
      data: { value: this.filtersValue(), comuni: this.comuni(), province: this.province() },
      ariaLabel: 'Filtri',
    });
    this.filtersDialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      this.filtersDialogRef = undefined;
      if (value) {
        this.applyFilters(value);
      }
    });
  }

  applyFilters(value: NewCustomerFiltersValue): void {
    this.flow.applySearchFilter({ ...this.flow.searchFilter(), comune: value.comune || null, provincia: value.provincia || null });
  }

  confirm(): void {
    const company = this.flow.company();
    if (!company || this.flow.loading()) {
      return;
    }

    this.flow.confirmCompany(company).subscribe({
      error: (error: unknown) => {
        const message = this.flow.errorMessage(error);
        if (message) {
          this.snackBar.open(message, 'Chiudi', { duration: 5000 });
        }
      },
    });
  }

  private distinct(values: (string | null)[]): string[] {
    return [...new Set(values.filter((value): value is string => !!value))].sort();
  }
}
